/**
 * EL INFORME DEL MES (documento «El informe del mes», 1-09-2026).
 *
 * «Lo que recibe cuando manda el reporte mensual. Las dos pantallas como tienen que quedar.»
 *
 * Diez bloques y en un orden que no es casual: primero dónde está y qué le vas a decir,
 * después lo que ha pasado con su cuerpo (peso, medidas, grasa, fotos) y al final lo que ha
 * hecho él (cumplimiento, su día tipo, sus alimentos, sus extras).
 *
 * DOS REGLAS DEL DOCUMENTO QUE MANDAN SOBRE TODO LO DEMÁS:
 *
 *   1. «El informe no le pide nada.» Lo único que se puede tocar son los selectores de las
 *      fotos y el botón de guardar el desayuno como plantilla. Ni un campo, ni una
 *      pregunta, ni un «sube tus fotos»: para eso está el reporte.
 *   2. Es UNA pantalla en dos momentos, no dos pantallas. Al enviar sale con el hueco del
 *      feedback en gris y con la hora; cuando le contestas, «el mismo informe, con tu
 *      bloque arriba. No es otro documento: es éste, completado».
 *
 * Un bloque sin datos NO SALE. El informe es lo único que el cliente lee del mes, y una
 * tarjeta vacía con un guion dentro se lee como que la app no sabe de él.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { diaDeCalendario, ahoraDeLaApp } from '../../lib/horaEspana';
import { parDeDiasDistintos, FALTA_OTRO_DIA } from '../../lib/comparativaFotos';
import { num1 } from '../../lib/numeros';
import FotosDelFeedback from './FotosDelFeedback';

const ORANGE = '#FF671F';
const VERDE = '#22C55E';
const ROJO = '#EF4444';

const COLOR = { verde: 'text-emerald-500', rojo: 'text-red-500', gris: 'text-foreground/30' };

/**
 * EL HUECO DE UN BLOQUE QUE NO TIENE DATOS (Jesús, punto 7 del 9-09).
 *
 * Los bloques se caían enteros cuando no había nada que pintar, y el cliente no veía ni el
 * hueco: recibía un informe más corto y daba por hecho que el informe era así. Y no es un
 * caso raro -- él lo midió: a 155 de 170 les llega sin la gráfica del peso.
 *
 * NO ROMPE SU REGLA 1 («el informe no le pide nada»), y por eso el texto EXPLICA y no pide:
 * «Este mes no registraste ninguno», nunca «sube tu peso». Es la frase que propuso él.
 */
export const Hueco = ({ titulo, texto, testid }) => (
    <section className="rounded-2xl p-4 border border-dashed border-border/70 bg-card/40"
        data-testid={testid}>
        <p className="text-[13px] font-semibold text-muted-foreground">{titulo}</p>
        <p className="text-[13px] text-muted-foreground/80 mt-1">{texto}</p>
    </section>
);

export const Tarjeta = ({ titulo, derecha, children, testid, tono }) => (
    <section
        className={`rounded-2xl p-4 space-y-3 border ${
            tono === 'naranja' ? '' : 'bg-card border-border'}`}
        style={tono === 'naranja'
            ? { borderColor: `${ORANGE}66`, backgroundColor: `${ORANGE}0D` } : undefined}
        data-testid={testid}>
        {(titulo || derecha) && (
            <div className="flex items-baseline justify-between gap-3">
                {/* En la tarjeta naranja el rótulo va en naranja, que es como lo pinta su
                    maqueta: el mismo bloque, apagado mientras es el hueco y encendido
                    cuando ya lleva su texto. */}
                <p className={`text-[11px] font-bold uppercase tracking-wider ${
                    tono === 'naranja' ? '' : 'text-muted-foreground'}`}
                    style={tono === 'naranja' ? { color: ORANGE } : undefined}>
                    {titulo}
                </p>
                {derecha && (
                    <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground text-right">
                        {derecha}
                    </p>
                )}
            </div>
        )}
        {children}
    </section>
);

/** Una fila de «etiqueta ......... valor», que es la forma de medio informe. */
export const Fila = ({ que, valor, primera, testid }) => (
    <div className={`flex items-baseline justify-between gap-3 py-2 ${
        primera ? '' : 'border-t border-border'}`} data-testid={testid}>
        <span className="text-sm text-foreground/80">{que}</span>
        {/* El número no se parte nunca: «82,8 / kg» en dos líneas se lee como dos datos. */}
        <span className="text-sm font-bold text-foreground tabular-nums text-right whitespace-nowrap">
            {valor}
        </span>
    </div>
);

/**
 * La curva del peso: TODA su historia, con el mes del informe encendido encima.
 *
 * DECISIÓN DE FRANCISCO, 7-09-2026. La maqueta de Jesús rotula la curva «4 jun · 2 jul ·
 * 4 ago · 1 sep» dentro de un informe que va del 4 de agosto al 1 de septiembre: son tres
 * meses. Aquí se dibujaba solo el mes, y así 83 clientes de 185 no veían línea ninguna
 * -- se pesan una vez al mes y hacen falta dos puntos -- y otros 80 veían una recta entre
 * dos. Con la historia entera se quedan sin curva 19, los que solo se han pesado una vez.
 *
 * El mes no se pierde: va en verde encendido sobre la línea apagada de todo lo anterior, y
 * los círculos son sus pesajes más el primero de todos, el día que entró. Las cinco filas
 * de debajo y el reparto semanal siguen siendo del mes; esto es solo la línea.
 *
 * Los cuatro rótulos son las fechas que sitúan la línea, no los ejes de una gráfica.
 */
const CurvaDePeso = ({ puntos }) => {
    const ps = (puntos || [])
        .filter(p => p && p.fecha && Number.isFinite(Number(p.valor)))
        .map(p => ({
            ...p,
            valor: Number(p.valor),
            t: new Date(`${String(p.fecha).slice(0, 10)}T12:00:00`).getTime(),
        }))
        .filter(p => Number.isFinite(p.t));
    if (ps.length < 2) return null;

    const ancho = 300, alto = 64;
    const vs = ps.map(p => p.valor);
    const min = Math.min(...vs), max = Math.max(...vs);
    const rango = max - min || 1;
    // EL EJE DE ABAJO ES EL TIEMPO, no el número de pesajes. Repartiendo por índice, diez
    // pesajes de una semana ocupaban lo mismo que un año entero, y con la historia completa
    // eso deforma la línea hasta mentir.
    const t0 = ps[0].t, t1 = ps[ps.length - 1].t;
    const rangoT = (t1 - t0) || 1;
    const xy = (p) => [
        ((p.t - t0) / rangoT) * ancho,
        alto - 6 - ((p.valor - min) / rango) * (alto - 12),
    ];
    const linea = (arr) => arr.map(p => xy(p).join(',')).join(' ');

    // Los informes YA GUARDADOS no traen la marca del mes: entonces se pinta todo encendido,
    // que es exactamente como se veían antes.
    const hayMarca = ps.some(p => p.en_el_mes);
    const delMes = hayMarca ? ps.filter(p => p.en_el_mes) : ps;
    const conCirculo = hayMarca ? [...new Set([ps[0], ...delMes])] : ps;

    // Con más de cuatro meses a la vista, «4 jun» no distingue un año de otro. El año va
    // entero y no en dos cifras: «abr 26» se lee como el 26 de abril.
    const dias = rangoT / 86400000;
    const fecha = (f) => {
        const d = new Date(`${String(f).slice(0, 10)}T12:00:00`);
        if (isNaN(d)) return '';
        return d.toLocaleDateString('es-ES', dias > 120
            ? { month: 'short', year: 'numeric' }
            : { day: 'numeric', month: 'short' }).replace('.', '');
    };
    // Cuatro fechas repartidas EN EL TIEMPO, para que caigan donde cae su punto.
    const enElTiempo = (parte) => {
        const objetivo = t0 + rangoT * parte;
        return ps.reduce((mejor, p, i) =>
            Math.abs(p.t - objetivo) < Math.abs(ps[mejor].t - objetivo) ? i : mejor, 0);
    };
    const idx = [...new Set([0, enElTiempo(1 / 3), enElTiempo(2 / 3), ps.length - 1])];

    return (
        <div data-testid="informe-curva">
            <svg viewBox={`0 0 ${ancho} ${alto}`} className="w-full" style={{ height: alto }}
                preserveAspectRatio="none" aria-hidden="true">
                <polyline fill="none" stroke={VERDE} strokeOpacity={hayMarca ? 0.35 : 1}
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke" points={linea(ps)} />
                {/* El mes, más gordo que el resto: en un cliente con un año de historia
                    ocupa una octava parte del ancho, y si va del mismo grosor se pierde. */}
                {hayMarca && delMes.length > 1 && (
                    <polyline fill="none" stroke={VERDE} strokeWidth="3" strokeLinecap="round"
                        strokeLinejoin="round" vectorEffect="non-scaling-stroke"
                        points={linea(delMes)} />
                )}
                {conCirculo.map((p, i) => {
                    const [x, y] = xy(p);
                    return (
                        <circle key={`${p.fecha}-${i}`} cx={x} cy={y} r="3" fill="none"
                            stroke={VERDE} strokeWidth="2" vectorEffect="non-scaling-stroke" />
                    );
                })}
            </svg>
            <div className="flex justify-between mt-1">
                {idx.map(i => (
                    <span key={i} className="text-[13px] text-muted-foreground">
                        {fecha(ps[i].fecha)}
                    </span>
                ))}
            </div>
        </div>
    );
};

/**
 * EL FEEDBACK DEL ENTRENADOR, con sus fotos dentro y la firma. Mientras es el hueco
 * (`pendiente`), en gris con el aviso. Lo pintan este informe y el del artefacto del 27-10
 * (`InformeConCheckin`).
 */
export const FeedbackDelInforme = ({ feedback, api, fotosDelFeedback,
                                    titulo = 'Tu feedback y tu programa nuevo' }) => (
    feedback?.pendiente ? (
        <Tarjeta titulo={titulo}
            testid="informe-feedback-pendiente">
            <p className="text-sm text-muted-foreground">{feedback.aviso}</p>
        </Tarjeta>
    ) : feedback?.texto ? (
        <Tarjeta titulo={titulo} tono="naranja"
            testid="informe-feedback">
            <p className="text-base text-foreground leading-snug whitespace-pre-wrap break-words">{feedback.texto}</p>
            {/* Y LAS FOTOS QUE LE ADJUNTA, DENTRO DEL FEEDBACK (Francisco, 22-10): iban
                debajo del informe, fuera de él. */}
            {api && <FotosDelFeedback api={api} fotos={fotosDelFeedback} />}
            <div className="flex items-center gap-2 pt-1">
                {feedback.iniciales && (
                    <span className="w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold text-white"
                        style={{ backgroundColor: ORANGE }}>
                        {feedback.iniciales}
                    </span>
                )}
                <span className="text-[13px] text-muted-foreground">
                    {feedback.firma}{feedback.fecha_label ? ` · ${feedback.fecha_label}` : ''}
                </span>
            </div>
        </Tarjeta>
    ) : null
);

/**
 * 6 · Tus fotos. Dos, y las elige él: es uno de los dos sitios donde puede tocar algo.
 *
 * `sinTitulo`: sin su rótulo, para ir dentro de una sección que ya lo tiene (el informe del
 * 28-10, «Tus fotos y tus medidas»). `onPar(izq, der)`: los días (de España) de las dos fotos
 * que se están comparando, para comparar debajo sus medidas; `izq` null si solo hay un día.
 */
export const TusFotos = ({ api, token, tipo, sinTitulo = false, onPar }) => {
    const [fotos, setFotos] = useState(null);
    const [pose, setPose] = useState('frente');
    const [izq, setIzq] = useState('');
    const [der, setDer] = useState('');
    const [urls, setUrls] = useState({});

    useEffect(() => {
        api.get('/reports/photos')
            .then(r => setFotos(r.data?.photos || []))
            .catch(() => setFotos([]));
    }, [api]);

    const dePose = (fotos || []).filter(f => (f.pose || 'frente') === pose && f.taken_at)
        .sort((a, b) => String(a.taken_at).localeCompare(String(b.taken_at)));

    // Al cambiar de pose se vuelve a la comparación de siempre, la primera contra la última
    // (la elige `parDeDiasDistintos` cuando no hay nada elegido). A partir de ahí manda él.
    useEffect(() => { setIzq(''); setDer(''); }, [pose, fotos]);

    // EL PAR, DE DOS DÍAS DISTINTOS (Francisco, 21-10). La regla vive en
    // lib/comparativaFotos: cada lado ofrece solo días anteriores (izquierda) o posteriores
    // (derecha) al del otro, nunca el mismo; y con fotos de un solo día no hay par.
    const par = parDeDiasDistintos(dePose, izq, der);
    const idIzq = par.izq?.id ?? '';
    const idDer = par.der?.id ?? '';

    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const traer = useCallback((id) => {
        if (!id || urls[id]) return;
        // Encogida, como las demás (12-09). Aquí un poco mayor que en la lista: la del
        // informe se mira despacio y es la que se compara con la del mes anterior.
        fetch(`${base}/api/reports/photos/${id}?w=1440`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => (r.ok ? r.blob() : Promise.reject(new Error('no'))))
            .then(b => setUrls(u => ({ ...u, [id]: URL.createObjectURL(b) })))
            .catch(() => {});
    }, [base, token, urls]);
    useEffect(() => { traer(idIzq); traer(idDer); }, [idIzq, idDer, traer]);

    const diaIzq = par.izq && !par.faltaOtroDia ? diaDeCalendario(par.izq.taken_at) : null;
    const diaDer = par.der ? diaDeCalendario(par.der.taken_at) : null;
    useEffect(() => { if (onPar) onPar(diaIzq, diaDer); }, [diaIzq, diaDer, onPar]);

    if (fotos === null) return null;     // todavia cargando: ni bloque ni hueco
    // SIN FOTOS, EL HUECO CON SU PORQUE (Jesús, punto 7 del 9-09). Antes el bloque
    // desaparecía entero y el cliente ni sabía que existía. El texto explica, no pide, que
    // es lo que manda su regla 1: «el informe no le pide nada».
    if (!fotos.length) {
        return (
            !pideMedidasYFotos(tipo) ? null :
            <Hueco testid="informe-fotos-vacio" titulo="Tus fotos"
                texto={delPeriodo(tipo).el
                    ? `Aquí irían tus fotos de ${delPeriodo(tipo).el}. `
                      + `No subiste ninguna ${delPeriodo(tipo).este}.`
                    : 'Aquí irían tus fotos. No subiste ninguna.'} />
        );
    }

    const cuando = (f) => {
        // El dia de España de la foto, como el resto del informe (14-10, REP-05): cortando el
        // instante, la subida a las 00:55 decia «13 de octubre» en un informe del 14.
        const d = new Date(`${diaDeCalendario(f.taken_at) || ''}T12:00:00`);
        return isNaN(d) ? f.taken_at
            : d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
    };

    /*
     * EL «ANTES» NO PUEDE IR DESPUÉS DEL «DESPUÉS» (Jesús, punto 38 de «La app, paso a
     * paso»: «en orden, y que el de la izquierda no pueda ser posterior al de la derecha»).
     * Y DESDE EL 21-10 TAMPOCO EL MISMO DÍA (Francisco): antes se dejaban elegibles en los dos
     * lados las fotos del mismo día, y con fotos de un solo día el informe abría con ese día
     * contra sí mismo. Lo resuelve `parDeDiasDistintos`; aquí solo se pinta.
     *
     * El id se compara como TEXTO: el que elige el cliente llega del `<select>` siempre como
     * cadena, y el de la lista es el que venga del servidor.
     */
    return (
        <Tarjeta titulo={sinTitulo ? undefined : 'Tus fotos'} testid="informe-fotos">
            <div className="flex gap-2">
                {/* LA POSE SE GUARDA EN SINGULAR, «espalda». El botón decía «espaldas» y por
                    eso salía apagado SIEMPRE: no hay ni habrá una sola foto con esa pose --
                    `routes/checkins.py` solo acepta frente, espalda y perfil, y lo que no
                    esté en esa lista lo guarda sin pose. El rótulo sí es «Espaldas», que es
                    como se dice; lo que tenía que casar es la clave. */}
                {[['frente', 'Frente'], ['espalda', 'Espaldas'], ['perfil', 'Perfil']].map(([v, l]) => {
                    const hay = (fotos || []).some(f => (f.pose || 'frente') === v);
                    const activo = pose === v;
                    return (
                        <button key={v} type="button" disabled={!hay}
                            onClick={() => setPose(v)} data-testid={`informe-pose-${v}`}
                            className={`px-3 py-1.5 rounded-full text-[13px] font-bold transition-colors disabled:opacity-30 ${
                                activo ? 'text-white' : 'bg-muted text-foreground/60'}`}
                            style={activo ? { backgroundColor: ORANGE } : undefined}>
                            {l}
                        </button>
                    );
                })}
            </div>

            {dePose.length && par.faltaOtroDia ? (
                <>
                    <div className="grid grid-cols-2 gap-2">
                        <div className="aspect-[3/4] rounded-xl overflow-hidden bg-muted" data-testid="informe-foto-unica">
                            {urls[idDer] && <img src={urls[idDer]} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="aspect-[3/4] rounded-xl border border-dashed border-border flex items-center justify-center p-3 text-center">
                            <p className="text-[13px] text-muted-foreground" data-testid="informe-fotos-falta-otro-dia">{FALTA_OTRO_DIA}</p>
                        </div>
                    </div>
                    <p className="text-[13px] text-muted-foreground">{cuando(par.der)}</p>
                </>
            ) : dePose.length ? (
                <>
                    <div className="grid grid-cols-2 gap-2">
                        {[[idIzq, setIzq, 'izq', par.izquierdas], [idDer, setDer, 'der', par.derechas]]
                            .map(([valor, poner, lado, elegibles]) => (
                            <select key={lado} value={valor} onChange={(e) => poner(e.target.value)}
                                data-testid={`informe-foto-${lado}`}
                                className="bg-muted border border-border rounded-xl px-3 py-2 text-[13px] text-foreground outline-none focus:border-[#FF671F]">
                                {elegibles.map(f => (
                                    <option key={f.id} value={f.id}>{cuando(f)}</option>
                                ))}
                            </select>
                        ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        {[idIzq, idDer].map((id, i) => (
                            <div key={i} className="aspect-[3/4] rounded-xl overflow-hidden bg-muted">
                                {urls[id] && <img src={urls[id]} alt="" className="w-full h-full object-cover" />}
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <p className="text-sm text-muted-foreground">
                    De esta pose todavía no hay ninguna.
                </p>
            )}
        </Tarjeta>
    );
};

/**
 * «GUÁRDAMELA COMO PLANTILLA» (bloque 8 de su documento).
 *
 * Guarda la combinación que él ya reconoce como su desayuno -- los alimentos del día tipo,
 * con las cantidades que enseña la fila, que son la MEDIANA del mes -- como una favorita de
 * COMIDA, que es la que luego se pone de un toque en cualquier comida desde Nutrición.
 *
 * El nombre se pone solo y con la fecha detrás: pedirle que escriba uno sería pedirle algo,
 * y el informe no le pide nada. Si ya tiene una con ese nombre, el servidor no protesta
 * (eso solo lo mira la pantalla de favoritas del día), así que la fecha las distingue.
 */
const ComoPlantilla = ({ api, fila }) => {
    const [estado, setEstado] = useState('');       // '' | 'guardando' | 'hecho'
    const guardar = async () => {
        if (estado) return;
        setEstado('guardando');
        try {
            await api.post('/diets/favorites', {
                name: `${fila.nombre} de siempre · ${ahoraDeLaApp().toLocaleDateString('es-ES',
                    { day: 'numeric', month: 'long' })}`,
                ambito: 'comida',
                comida: fila.clave,
                alimentos: (fila.items || [])
                    .filter((x) => x.alimento_id != null)
                    .map((x) => ({ alimento_id: x.alimento_id, nombre: x.nombre,
                                   cantidad_g: x.gramos })),
            });
            setEstado('hecho');
        } catch (e) {
            console.error('[informe] no se pudo guardar la plantilla', e);
            setEstado('');
            toast.error('No hemos podido guardarla. Inténtalo de nuevo.');
        }
    };
    // Ya guardada, el botón se queda diciendo dónde está: guardarla dos veces no aporta
    // nada y dejar el botón igual invita a hacerlo.
    if (estado === 'hecho') {
        return (
            <span className="mt-2 block text-[13px]" style={{ color: VERDE }}
                data-testid="informe-plantilla-hecha">
                Guardada · La tienes en tus comidas favoritas
            </span>
        );
    }
    return (
        <button type="button" onClick={guardar} disabled={estado === 'guardando'}
            data-testid="informe-guardar-plantilla"
            className="mt-2 rounded-full border px-3 py-1.5 text-[13px] font-bold transition-colors
                       disabled:opacity-60"
            style={{ borderColor: ORANGE, color: ORANGE }}>
            {estado === 'guardando' ? 'Guardando...' : 'Guárdamela como plantilla'}
        </button>
    );
};

/**
 * EL INFORME SE LLAMA COMO EL REPORTE QUE LO GENERA (Francisco, 11-09).
 *
 * «El informe de un reporte quincenal se llama "informe mensual".»
 *
 * El título estaba escrito a mano porque cuando se hizo esta pantalla el informe solo lo
 * generaba el mensual. Ahora lo generan los tres, y el tipo viaja dentro del informe
 * (`core/informe_mensual.montar_informe`). Los informes YA GUARDADOS no lo traen -- se
 * congelan al enviar el reporte --, y entonces se dice «Tu informe» a secas: no se adivina.
 */
const TITULO = {
    quincenal: 'Tu informe quincenal',
    mensual: 'Tu informe mensual',
    semanal: 'Tu informe semanal',
};

/**
 * Y EL PERIODO, POR SU NOMBRE (Francisco, 13-09: «que diga quincena en vez de mes cuando
 * sea quincenal»). El título ya se llamaba por su tipo, pero por dentro el informe seguía
 * hablando de mes en todas partes: «Empezaste el mes en», «tus fotos del mes», «las veces
 * que las has puesto este mes». En un quincenal de siete días eso cuenta otra cosa.
 *
 * Sin tipo -- los informes ya guardados no lo traen -- se quita la coletilla y la frase se
 * queda sin periodo: «Empezaste en», «Aquí irían tus fotos». Se lee bien y no miente.
 * El rótulo de la esquina («−2,8 kg esta quincena») lo escribe el servidor con las mismas
 * palabras, en `core/informe_del_mes.palabras_del_periodo`.
 */
const PERIODO = {
    quincenal: { el: 'la quincena', este: 'esta quincena' },
    mensual: { el: 'el mes', este: 'este mes' },
    semanal: { el: 'la semana', este: 'esta semana' },
};
const delPeriodo = (tipo) => PERIODO[tipo] || { el: '', este: '' };
// "Esta quincena no..." al principio de una frase; sin tipo, la frase empieza por el "No".
const enEsteAlEmpezar = (tipo, resto) => {
    const p = delPeriodo(tipo).este;
    return p ? `${p[0].toUpperCase()}${p.slice(1)} ${resto}` : `${resto[0].toUpperCase()}${resto.slice(1)}`;
};
// LAS MEDIDAS Y LAS FOTOS SON DEL MENSUAL (12-10). En el quincenal y en el semanal no se
// piden, así que su informe no enseña el hueco de "aquí irían": le decía "no las tomaste" de
// algo que nadie le pidió. Sin tipo (los informes ya guardados) se queda como estaba.
const pideMedidasYFotos = (tipo) => tipo !== 'quincenal' && tipo !== 'semanal';

const InformeDelMes = ({ informe, api, token, fotosDelFeedback }) => {
    const b = informe?.bloques;
    if (!b) return null;

    const { periodo, feedback, peso, medidas, hecho,
            dia_tipo: dia, preferencias, extras } = b;

    return (
        <div className="space-y-3" data-testid="informe-del-mes">
            {/* ── CABECERA ── */}
            <div>
                {periodo?.label && (
                    <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: ORANGE }}>
                        {periodo.label}
                    </p>
                )}
                <h2 className="text-2xl font-bold text-foreground uppercase"
                    style={{ fontFamily: 'Barlow Condensed', letterSpacing: '0.02em' }}>
                    {TITULO[informe?.tipo] || 'Tu informe'}
                </h2>
            </div>

            {/* «TU OBJETIVO · TU CICLO» YA NO SALE (27-10). */}

            {/* ── 2 · TU FEEDBACK Y TU PROGRAMA NUEVO ── */}
            {/* CON SU RÓTULO, que su maqueta lo pinta en LOS DOS momentos -- en gris cuando
                es el hueco y en naranja cuando ya está escrito -- y aquí no salía en ninguno.
                Sin él, el aviso de «estamos revisando» se lee como una nota suelta debajo del
                ciclo y no como el bloque 2, que es lo que luego se completa. */}
            <FeedbackDelInforme feedback={feedback} api={api} fotosDelFeedback={fotosDelFeedback} />

            {/* ── 3 · TU PESO ── */}
            {!peso?.hay && (
                <Hueco testid="informe-peso-vacio" titulo="Tu peso"
                    texto={`Aquí iría la evolución de tu peso. ${enEsteAlEmpezar(informe?.tipo, 'no registraste ninguno.')}`} />
            )}
            {peso?.hay && (
                <Tarjeta titulo="Tu peso" derecha={peso.titulo_cambio} testid="informe-peso">
                    {/* `curva` es la historia entera con el mes marcado; `serie` (solo el
                        mes) es lo que traen los informes guardados de antes. */}
                    <CurvaDePeso puntos={peso.curva || peso.serie} />
                    <div className="-my-2">
                        <Fila primera valor={peso.empezaste_label}
                            que={delPeriodo(informe?.tipo).el
                                ? `Empezaste ${delPeriodo(informe?.tipo).el} en` : 'Empezaste en'} />
                        <Fila que="Lo acabas en" valor={peso.acabas_label} />
                        {/* «DESDE TU ÚLTIMO REPORTE: 0 KG» CUANDO EL ANTERIOR ERA UN KILO
                            MÁS (Francisco, 11-09). Aquí salía `cambio_label`, que es el
                            cambio DENTRO del mes: al que se pesó una sola vez, el primero
                            y el último son el mismo pesaje y la fila cantaba un cero.
                            `desde_el_reporte_label` cuenta desde donde estaba en el
                            reporte anterior, que es lo que dice la fila. Los informes
                            guardados de antes no lo traen y se quedan con el de siempre. */}
                        <Fila que="Desde tu último reporte"
                            valor={peso.desde_el_reporte_label || peso.cambio_label} />
                        {peso.al_empezar_label && (
                            <Fila que="Cuando empezaste pesabas" valor={peso.al_empezar_label} />
                        )}
                        {peso.desde_el_principio_label && (
                            <Fila que="Desde que empezaste" valor={peso.desde_el_principio_label} />
                        )}
                    </div>
                    {(peso.por_semana || []).length > 0 && (
                        <div className="pt-2 border-t border-border">
                            {/* «BAJANDO» SOLO SI BAJA. La frase era fija y a un cliente de
                                volumen le decía «lo que has ido bajando» encima de un
                                «+2,1 kg este mes»: el mismo bloque contando dos cosas
                                contrarias. El reparto es del cambio, sea en la dirección
                                que sea. */}
                            {/* EN KILOS CUANDO EL PORCENTAJE NO DICE NADA. El servidor manda
                                `pct: null` en los meses que suben y bajan y acaban casi
                                igual: ahí la parte se calcula sobre un total diminuto y se
                                dispara («Semana 2 · 390 %»). Los kilos de cada semana
                                contestan la misma pregunta y son verdad siempre. */}
                            <p className="text-[13px] text-muted-foreground mb-1">
                                {peso.por_semana[0]?.pct == null
                                    ? `Lo que se movió tu peso cada semana. ${enEsteAlEmpezar(informe?.tipo, 'subió y bajó, así que el reparto en porcentaje no diría nada.')}`
                                    : `Porcentaje del peso total que has ido ${peso.cambio < 0 ? 'bajando' : 'subiendo'} por semana.`}
                            </p>
                            <div className="-my-2">
                                {peso.por_semana.map((s, i) => (
                                    // Con el menos de verdad, no un guion: es la misma regla
                                    // que `_con_signo` en el servidor, y en esta tarjeta se
                                    // leían los dos signos a la vez («−2,8 kg» y «-290 %»).
                                    <Fila key={s.semana} primera={i === 0}
                                        que={`Semana ${s.semana}`}
                                        valor={s.pct == null
                                            ? `${s.kg < 0 ? '−' : s.kg > 0 ? '+' : ''}${Math.abs(s.kg).toFixed(1).replace('.', ',')} kg`
                                            : `${s.pct < 0 ? '−' : ''}${Math.abs(s.pct)} %`} />
                                ))}
                            </div>
                        </div>
                    )}
                </Tarjeta>
            )}

            {/* ── 4 · TUS MEDIDAS ── */}
            {!medidas?.hay && pideMedidasYFotos(informe?.tipo) && (
                <Hueco testid="informe-medidas-vacio" titulo="Tus medidas"
                    texto="Aquí irían tus diez medidas. Este mes no las tomaste." />
            )}
            {/* LA PRIMERA TOMA NO TIENE CON QUÉ COMPARARSE (14-10, REP-04). Las dos columnas son
                diferencias (contra el mes pasado y contra la primera toma), así que en su primer
                informe salían diez filas con una raya en las dos, justo después de medirse. Sin
                ninguna comparación se enseñan las medidas que mandó, que la API ya trae en
                `valor`, y se le dice cuándo verá el cambio. */}
            {medidas?.hay && !medidas.filas.some(f => f.mes || f.primera) && (
                <Tarjeta titulo="Tus medidas" testid="informe-medidas">
                    <div className="-my-2">
                        {medidas.filas.map((f, i) => (
                            <Fila key={f.clave} primera={i === 0} que={f.etiqueta}
                                valor={`${num1(f.valor)} cm`} />
                        ))}
                    </div>
                </Tarjeta>
            )}
            {medidas?.hay && medidas.filas.some(f => f.mes || f.primera) && (
                <Tarjeta titulo="Tus medidas" testid="informe-medidas">
                    {/* CADA DIFERENCIA CON SU PUNTO DE PARTIDA (Francisco, 22-10: «sale -10 sin
                        referencia»): la medida de hoy en su columna y, debajo de cada cambio, la
                        de entonces. «Mes ant.» es el último reporte con medidas. En el móvil el
                        nombre va en su renglón y las tres cifras debajo: en una sola fila no
                        cabían y partían «Brazo derecho relajado» letra a letra. */}
                    <div className="grid grid-cols-3 sm:grid-cols-[minmax(0,1fr)_3.5rem_4rem_4rem] gap-x-2 gap-y-0 items-start">
                        <span className="col-span-3 sm:col-span-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                            Diez tomas
                        </span>
                        <span className="text-[13px] text-muted-foreground text-right">Ahora</span>
                        <span className="text-[13px] text-muted-foreground text-right">Mes ant.</span>
                        <span className="text-[13px] text-muted-foreground text-right">1ª toma</span>
                        {medidas.filas.map(f => (
                            <React.Fragment key={f.clave}>
                                <span className="col-span-3 sm:col-span-1 text-sm text-foreground/80 border-t border-border pt-2 sm:pb-2">
                                    {f.etiqueta}
                                </span>
                                <span className="text-sm tabular-nums text-right text-foreground/80 sm:border-t border-border pb-2 sm:pt-2"
                                    data-testid={`medida-ahora-${f.clave}`}>
                                    {num1(f.valor)}
                                </span>
                                {['mes', 'primera'].map(cual => (
                                    <span key={cual} className="text-right sm:border-t border-border pb-2 sm:pt-2">
                                        <span className={`block text-base font-bold tabular-nums leading-tight ${
                                            f[cual] ? COLOR[f[cual].color] : 'text-foreground/20'}`}>
                                            {f[cual] ? f[cual].label : '-'}
                                        </span>
                                        {f[cual]?.antes != null && (
                                            <span className="block text-[11px] text-muted-foreground tabular-nums"
                                                data-testid={`medida-antes-${cual}-${f.clave}`}>
                                                de {num1(f[cual].antes)}
                                            </span>
                                        )}
                                    </span>
                                ))}
                            </React.Fragment>
                        ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground pt-1">En centímetros.</p>
                </Tarjeta>
            )}

            {/* «Tu porcentaje de grasa» ya no sale (Francisco, 22-10). */}

            {/* ── 6 · TUS FOTOS ── */}
            {api && <TusFotos api={api} token={token} tipo={informe?.tipo} />}

            {/* ── 7 · LO QUE HAS HECHO ── */}
            {hecho?.hay && (
                <Tarjeta titulo="Lo que has hecho" testid="informe-hecho">
                    <div className="-my-1">
                        {hecho.filas.map((f, i) => (
                            <div key={f.clave} data-testid={`informe-hecho-${f.clave}`}
                                className={`grid grid-cols-2 gap-3 py-2.5 ${
                                    i ? 'border-t border-border' : ''}`}>
                                {/* Sin su contrario al lado, la fila va a lo ancho (21-10): en
                                    media columna «Días desde tu último reporte» no cabía. */}
                                <span className={`flex items-baseline justify-between gap-2 ${
                                    f.valor2 == null ? 'col-span-2' : ''}`}>
                                    <span className="text-sm text-foreground/80">{f.etiqueta}</span>
                                    <span className="text-sm font-bold text-foreground tabular-nums">{f.valor}</span>
                                </span>
                                {f.valor2 != null && (
                                    <span className="flex items-baseline justify-between gap-2">
                                        <span className="text-sm text-foreground/80">{f.etiqueta2}</span>
                                        <span className="text-sm font-bold text-foreground tabular-nums">{f.valor2}</span>
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </Tarjeta>
            )}

            {/* ── 8 · TU DÍA TIPO ── */}
            {dia?.hay && (
                <Tarjeta titulo="Tu día tipo" testid="informe-dia-tipo">
                    <div className="-my-1">
                        {dia.filas.map((f, i) => (
                            <div key={f.clave} data-testid={`informe-dia-${f.clave}`}
                                className={`grid grid-cols-[4.5rem_1fr] gap-3 py-3 ${
                                    i ? 'border-t border-border' : ''}`}>
                                <span className="text-[13px] font-bold" style={{ color: ORANGE }}>
                                    {f.momento}
                                </span>
                                <span>
                                    <span className="block text-sm font-bold text-foreground">{f.nombre}</span>
                                    <span className="block text-[13px] text-muted-foreground">{f.texto}</span>
                                    <span className="block text-base text-foreground/70 mt-0.5">{f.cuantos}</span>
                                    {/* EL ÚNICO BOTÓN DEL INFORME, ADEMÁS DE LOS SELECTORES DE
                                        LAS FOTOS. Su documento lo dice dos veces: la maqueta lo
                                        dibuja bajo el desayuno y el pie remata «lo único que
                                        puede tocar son los selectores de las fotos y el botón de
                                        guardar SU DESAYUNO como plantilla». Por eso va en la
                                        primera comida del día y no en todas: ponerlo en las seis
                                        convertiría el informe en una pantalla de botones, que es
                                        justo lo que él no quiere.
                                        Solo cuando esa comida es una de verdad: si «cambia casi
                                        cada día» no hay plantilla que guardar. */}
                                    {i === 0 && !f.varia && (f.items || []).some(x => x.alimento_id != null) && (
                                        <ComoPlantilla api={api} fila={f} />
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                </Tarjeta>
            )}

            {/* ── 9 · PREFERENCIAS CONCRETAS DE ALIMENTOS ── */}
            {preferencias?.hay && (
                <Tarjeta titulo="Preferencias concretas de alimentos" testid="informe-preferencias">
                    {/* EN SINGULAR, COMO TODA LA APP (Francisco, 11-09: «"Proteínas /
                        Hidratos / Grasas" en plural... En toda la app es singular»). El
                        arreglo del 11-09 pasó por Mis macros, el comparador, el chat y la
                        calculadora, y este rótulo del informe se quedó en «Grasas». La
                        clave del dato (`grasas`) no se toca: es la que manda el servidor. */}
                    {[['proteina', 'Proteína'], ['hidratos', 'Hidratos'], ['grasas', 'Grasa']].map(([k, l]) => (
                        (preferencias[k] || []).length > 0 && (
                            <div key={k} data-testid={`informe-preferencias-${k}`}>
                                <p className="text-[13px] font-bold mb-1" style={{ color: ORANGE }}>{l}</p>
                                <div className="-my-1">
                                    {preferencias[k].map((a, i) => (
                                        <Fila key={a.nombre} primera={i === 0}
                                            que={a.nombre} valor={a.label} />
                                    ))}
                                </div>
                            </div>
                        )
                    ))}
                </Tarjeta>
            )}

            {/* ── 10 · EXTRAS REGISTRADOS ── */}
            {!extras?.hay && (
                <Hueco testid="informe-extras-vacio" titulo="Tus extras"
                    texto={`Aquí iría lo que comiste fuera de la dieta. ${enEsteAlEmpezar(informe?.tipo, 'no apuntaste nada.')}`} />
            )}
            {extras?.hay && (
                <Tarjeta titulo="Extras registrados" testid="informe-extras">
                    <p className="text-[13px] text-muted-foreground -mt-1">{extras.titulo}</p>
                    <div className="-my-1">
                        {extras.lista.map((e, i) => (
                            <div key={`${e.fecha}-${i}`}
                                className={`flex items-baseline gap-3 py-2 ${
                                    i ? 'border-t border-border' : ''}`}>
                                {/* Con el mes cuando la lista cruza dos («Mar 21 oct»): cabe
                                    en 5,5 rem. La cantidad, la de los extras del catálogo
                                    (21-10); los informes viejos no la traen. */}
                                <span className="text-[13px] text-muted-foreground w-[5.5rem] shrink-0"
                                    data-testid="informe-extra-dia">
                                    {e.dia_label}
                                </span>
                                <span className="text-sm text-foreground min-w-0 break-words">
                                    {e.texto}{e.cantidad ? ` · ${e.cantidad}` : ''}
                                </span>
                            </div>
                        ))}
                    </div>
                </Tarjeta>
            )}
        </div>
    );
};

export default InformeDelMes;
